import z from "zod";

export const hotelPlanningDepartureUserInfoSchema = z.object({
    userId: z.string(),
    userName: z.string(),
});

export enum PatientHotelDepartureStatuses {
    PENDING = "PENDING",
    IN_PROGRESS = "IN_PROGRESS",
    COMPLETED = "COMPLETED",
}

/** Source of a departure row: checkpoint-driven clinic passenger vs transport-request custom passenger. */
export const hotelDeparturePassengerTypeSchema = z.enum(["clinic", "custom"]);

/** Fields shared by both clinic and custom departure rows. */
const hotelDepartureItemBaseSchema = z.object({
    name: z.string(),
    /** Nullable on history (`?date=` past day): transferred rows are kept even without a hotel. */
    hotelName: z.string().nullable(),
    waitingTime: z.string().nullable(),
    formattedWaitingTime: z.string().nullable(),
    exitedAt: z.string().nullable(),
    exitedBy: hotelPlanningDepartureUserInfoSchema.nullable(),
    enteredAt: z.string().nullable(),
    enteredBy: hotelPlanningDepartureUserInfoSchema.nullable(),
    status: z.nativeEnum(PatientHotelDepartureStatuses),
    assignedUser: hotelPlanningDepartureUserInfoSchema.nullable().optional(),
    labelledUser: hotelPlanningDepartureUserInfoSchema.nullable().optional(),
    companionsCount: z.number().nullable().optional(),
});

/** Clinic passenger: driven by the scenario-checkpoint system, identified + acted on by operationId + uniqueCheckpointId. */
export const hotelDepartureClinicItemSchema = hotelDepartureItemBaseSchema.extend({
    type: z.literal("clinic"),
    operationId: z.string(),
    uniqueCheckpointId: z.string(),
});

/** Custom passenger created from the transport-request panel. No checkpoint ids yet; identified by `id`. */
export const hotelDepartureCustomItemSchema = hotelDepartureItemBaseSchema.extend({
    type: z.literal("custom"),
    id: z.string().nullable(),
    /** transport-svc task id once a driver is assigned; null while still unassigned. */
    taskId: z.string().nullable(),
});

export const hotelDepartureResponseItemSchema = z.discriminatedUnion("type", [
    hotelDepartureClinicItemSchema,
    hotelDepartureCustomItemSchema,
]);

export const hotelPlanningDepartureResponseSchema = z.array(hotelDepartureResponseItemSchema);

/** Response shape of GET /clinic-hotel/passengers/:date — custom passengers only. */
export const hotelDepartureCustomResponseSchema = z.array(hotelDepartureCustomItemSchema);

export type IHotelPlanningDepartureUserInfo = z.infer<typeof hotelPlanningDepartureUserInfoSchema>;
export type IHotelPlanningDepartureClinicItem = z.infer<typeof hotelDepartureClinicItemSchema>;
export type IHotelPlanningDepartureCustomItem = z.infer<typeof hotelDepartureCustomItemSchema>;
export type IHotelPlanningDepartureResponseItem = z.infer<typeof hotelDepartureResponseItemSchema>;
export type IHotelPlanningDepartureResponse = z.infer<typeof hotelPlanningDepartureResponseSchema>;

/** Type guard: narrows a departure row to the clinic variant (has operationId + uniqueCheckpointId). */
export const isClinicDeparture = (row: IHotelPlanningDepartureResponseItem): row is IHotelPlanningDepartureClinicItem =>
    row.type === "clinic";

/** Stable grid row id for either variant: operationId for clinic, id for custom. */
export const getHotelDepartureRowId = (row: IHotelPlanningDepartureResponseItem): string =>
    row.type === "clinic" ? row.operationId : (row.id ?? "");
